/**
 * Zod schemas for the user-notification domain — every endpoint on
 * `routes/notificationRoute.js` that web or mobile consumes.
 *
 * WHY STRICT: the domain is completing its v1→v2 migration. The v1
 * columns (`type` RENTER/VENDOR, `key` ORDER/PAYMENT, `isRead`) are
 * dropped from the wire in the same batch that ships these schemas —
 * `.strict()` is the tripwire that keeps them from quietly returning.
 *
 * The event-type REGISTRY (constants/notificationTypes.js) stays
 * backend-only truth on purpose: `eventType` is validated as a string
 * here, not an enum — mirroring the registry keys in this package would
 * be a second copy that drifts. `category` IS an enum: five stable
 * values the registry organizes under.
 *
 * v1-era rows predate `category`/`eventType` and carry NULLs there —
 * clients render those degraded (one old row never breaks the list).
 */
import { z } from 'zod';
import { IsoDateTimeString, PAGE_SIZE_CHOICES, envelope } from './_primitives';

// ─── Route paths ────────────────────────────────────────────────────────

/**
 * Wire paths for the notification domain (see auth.ts AUTH_ROUTES for
 * the convention). Parameterized paths are builder functions so call
 * sites can't drift on the suffix.
 */
export const NOTIFICATION_ROUTES = {
  list: 'notifications',
  readAll: 'notifications/read-all',
  markRead: (id: number) => `notifications/${id}/read`,
  archive: (id: number) => `notifications/${id}/archive`,
  preferences: 'notifications/preferences',
  // Public, token-authed (GG-493): the unsubscribe links inside registry
  // emails. No session — the signed token IS the authorization.
  unsubscribe: 'notifications/unsubscribe',
  unsubscribeOneClick: 'notifications/unsubscribe/one-click',
  unsubscribePreferences: 'notifications/unsubscribe/preferences',
} as const;

// ─── Android push presentation (cross-app contract) ─────────────────────

/**
 * Two halves of one contract: the APP creates these notification
 * channels (notifee, at login) and ships the `ic_notification` status-bar
 * drawable; the BACKEND names the same channel/icon on OS-rendered
 * (notification-block) FCM sends. A mismatch fails SILENTLY on both
 * sides — FCM falls back to the auto-created "Miscellaneous" channel and
 * the launcher icon (the white-square small icon) — so both ends import
 * these values and never restate them.
 */
export const ANDROID_DEFAULT_CHANNEL_ID = 'gearghost-default';
export const ANDROID_MESSAGES_CHANNEL_ID = 'gearghost-messages';
/** Alpha-only silhouette in android/app/src/main/res/drawable-*dpi/. */
export const ANDROID_NOTIFICATION_SMALL_ICON = 'ic_notification';
/** primitive-brand-primary-300 ("Ectoplasm") — small-icon/accent tint. */
export const ANDROID_NOTIFICATION_ACCENT_COLOR = '#6fb4ba';

/**
 * The privacy policy notice's event type (docs/plans/notification-campaigns.md):
 * the backend registers it, and the mobile app routes a tap on it, from the
 * push or the notifications list, to its Privacy Policy screen (Decision 6).
 * Every push carries its event type beside the legacy routing key.
 */
export const PRIVACY_POLICY_UPDATED_EVENT = 'policy.privacy_updated';

/**
 * The receipt upload notice's event type (docs/plans/receipts-redesign.md,
 * Decisions 54 and 55): the backend registers it and tells a vendor when
 * every file of their upload is read, and the mobile app routes a tap on it
 * to Account Standing, since the review is on the web.
 */
export const RECEIPT_UPLOAD_FINISHED_EVENT = 'receipts.upload_finished';

/**
 * The vendor import notice's event type (docs/plans/vendor-api.md, Decision
 * 47): the backend tells a vendor when an import of their gear through the
 * vendor API is made, and the mobile app routes a tap on it to Account
 * Standing, as it does the receipt upload notice, since the Listings page is
 * on the web.
 */
export const VENDOR_IMPORT_FINISHED_EVENT = 'vendor_import.finished';

// ─── Shared primitives for the domain ───────────────────────────────────

export const NotificationCategorySchema = z.enum([
  'order',
  'payment',
  'payout',
  'review',
  'system',
  'account',
]);
export type NotificationCategory = z.infer<typeof NotificationCategorySchema>;

export const NotificationStatusSchema = z.enum(['UNREAD', 'READ', 'ARCHIVED']);
export type NotificationStatus = z.infer<typeof NotificationStatusSchema>;

/** Recipient scoping as stored on the row ('BOTH' = either role view). */
export const RecipientRoleSchema = z.enum(['VENDOR', 'RENTER', 'BOTH']);
export type RecipientRole = z.infer<typeof RecipientRoleSchema>;

// ─── Response payloads ──────────────────────────────────────────────────

/**
 * One in-app notification row as the list endpoint returns it.
 * `category`/`eventType` are nullable ONLY because v1-era rows predate
 * them — new rows always carry both. `payload` is registry-defined
 * per event and opaque to the transport layer.
 */
export const NotificationSchema = z
  .object({
    id: z.number().int(),
    title: z.string().nullable(),
    message: z.string(),
    category: NotificationCategorySchema.nullable(),
    eventType: z.string().min(1).nullable(),
    notiStatus: NotificationStatusSchema,
    actionUrl: z.string().nullable(),
    groupKey: z.string().nullable(),
    payload: z.unknown(),
    recipientRole: RecipientRoleSchema,
    createdAt: IsoDateTimeString,
    readAt: IsoDateTimeString.nullable(),
  })
  .strict();
export type Notification = z.infer<typeof NotificationSchema>;

/**
 * `GET /api/notifications` payload. `pageSize` is the server's real
 * page size — the pagination contract that keeps clients from
 * paginating by a guessed default (the trailing-empty-pages bug).
 */
export const NotificationListSchema = z
  .object({
    notifications: z.array(NotificationSchema),
    count: z.number().int().min(0),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
    totalPages: z.number().int().min(0),
  })
  .strict();
export type NotificationList = z.infer<typeof NotificationListSchema>;

/** Channel matrix for one event type. */
export const ChannelMatrixSchema = z
  .object({
    inApp: z.boolean(),
    email: z.boolean(),
    push: z.boolean(),
  })
  .strict();
export type ChannelMatrix = z.infer<typeof ChannelMatrixSchema>;

/**
 * One row of `GET /api/notifications/preferences`: the registry entry
 * (already filtered to the user's roles server-side) merged with the
 * user's overrides.
 */
export const NotificationPreferenceRowSchema = z
  .object({
    eventType: z.string().min(1),
    category: NotificationCategorySchema,
    label: z.string().min(1),
    roles: z.array(z.enum(['VENDOR', 'RENTER'])).min(1),
    channels: ChannelMatrixSchema,
  })
  .strict();
export type NotificationPreferenceRow = z.infer<
  typeof NotificationPreferenceRowSchema
>;

export const NotificationPreferencesSchema = z.array(
  NotificationPreferenceRowSchema,
);
export type NotificationPreferences = z.infer<
  typeof NotificationPreferencesSchema
>;

/** `PATCH /api/notifications/read-all` payload. */
export const MarkAllReadResultSchema = z
  .object({ updated: z.number().int().min(0) })
  .strict();
export type MarkAllReadResult = z.infer<typeof MarkAllReadResultSchema>;

/**
 * One email-flag row on the public unsubscribe surface (GG-493).
 * EMAIL ONLY by design — this token-authed surface never exposes the
 * inApp/push channels.
 */
export const UnsubscribeEmailPrefRowSchema = z
  .object({
    eventType: z.string().min(1),
    label: z.string().min(1),
    email: z.boolean(),
  })
  .strict();
export type UnsubscribeEmailPrefRow = z.infer<
  typeof UnsubscribeEmailPrefRowSchema
>;

export const UnsubscribeEmailPrefsSchema = z.array(
  UnsubscribeEmailPrefRowSchema,
);
export type UnsubscribeEmailPrefs = z.infer<typeof UnsubscribeEmailPrefsSchema>;

/**
 * `GET notifications/unsubscribe/preferences` payload: which event the
 * token TARGETS (so the landing page can name what its button will do
 * before anything happens) plus the account's full email-flag list.
 */
export const UnsubscribeLandingSchema = z
  .object({
    target: UnsubscribeEmailPrefRowSchema,
    preferences: UnsubscribeEmailPrefsSchema,
  })
  .strict();
export type UnsubscribeLanding = z.infer<typeof UnsubscribeLandingSchema>;

/** Result of an unsubscribe / email-flag flip: the row it changed. */
export const UnsubscribeResultSchema = UnsubscribeEmailPrefRowSchema;
export type UnsubscribeResult = z.infer<typeof UnsubscribeResultSchema>;

// ─── Request contracts ──────────────────────────────────────────────────

/**
 * Query params for `GET /api/notifications`. The legacy `type` filter
 * (v1 RENTER/VENDOR column) is gone — role scoping happens via
 * `recipientRole` on the rows; clients filter their Vendor/Renter
 * toggle against that.
 */
/**
 * Rows per page of the inbox when a request names none, as the app has
 * always asked for it, and the most one page may hold: the largest size a
 * web list offers (PAGE_SIZE_CHOICES).
 */
export const NOTIFICATIONS_DEFAULT_PAGE_SIZE = 20;
export const NOTIFICATIONS_MAX_PAGE_SIZE = Math.max(...PAGE_SIZE_CHOICES);

export const NotificationsQueryRequestSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  /** The page size, which the reply reports back (pageSize); web sends the one picked under its list. */
  limit: z.coerce.number().int().min(1).max(NOTIFICATIONS_MAX_PAGE_SIZE).default(NOTIFICATIONS_DEFAULT_PAGE_SIZE),
  category: NotificationCategorySchema.optional(),
  status: NotificationStatusSchema.optional(),
});
export type NotificationsQueryRequest = z.infer<
  typeof NotificationsQueryRequestSchema
>;

/**
 * Body for `PUT /api/notifications/preferences`. Clients send the FULL
 * per-event state on every save so partial updates can't desync;
 * membership/role validation stays server-side against the registry.
 */
export const UpdatePreferencesRequestSchema = z
  .object({
    preferences: z
      .array(
        z
          .object({
            eventType: z.string().min(1),
            inApp: z.boolean(),
            email: z.boolean(),
            push: z.boolean(),
          })
          .strict(),
      )
      .min(1),
  })
  .strict();
export type UpdatePreferencesRequest = z.infer<
  typeof UpdatePreferencesRequestSchema
>;

/**
 * Body for `POST /api/notifications/unsubscribe` (the landing page's
 * confirm). The token is the signed unsubscribe JWT from the email link;
 * it names the user AND the event type, so the body carries nothing else.
 */
export const UnsubscribeRequestSchema = z
  .object({ token: z.string().min(1) })
  .strict();
export type UnsubscribeRequest = z.infer<typeof UnsubscribeRequestSchema>;

/**
 * Body for `PUT /api/notifications/unsubscribe/preferences` — the
 * landing page's per-event email flip (ON or OFF: an accidental
 * unsubscribe must be reversible from the same page).
 */
export const SetUnsubscribePreferenceRequestSchema = z
  .object({
    token: z.string().min(1),
    eventType: z.string().min(1),
    email: z.boolean(),
  })
  .strict();
export type SetUnsubscribePreferenceRequest = z.infer<
  typeof SetUnsubscribePreferenceRequestSchema
>;

// ─── Envelopes + unwrap helpers ─────────────────────────────────────────

export const NotificationListEnvelopeSchema = envelope(NotificationListSchema);
export const NotificationPreferencesEnvelopeSchema = envelope(
  NotificationPreferencesSchema,
);
export const MarkAllReadEnvelopeSchema = envelope(MarkAllReadResultSchema);

export const unwrapNotificationList = (
  r: z.infer<typeof NotificationListEnvelopeSchema>,
): NotificationList => r.data;
export const unwrapNotificationPreferences = (
  r: z.infer<typeof NotificationPreferencesEnvelopeSchema>,
): NotificationPreferences => r.data;
export const unwrapMarkAllRead = (
  r: z.infer<typeof MarkAllReadEnvelopeSchema>,
): MarkAllReadResult => r.data;

export const UnsubscribeResultEnvelopeSchema = envelope(
  UnsubscribeResultSchema,
);
export const UnsubscribeLandingEnvelopeSchema = envelope(
  UnsubscribeLandingSchema,
);

export const unwrapUnsubscribeResult = (
  r: z.infer<typeof UnsubscribeResultEnvelopeSchema>,
): UnsubscribeResult => r.data;
export const unwrapUnsubscribeLanding = (
  r: z.infer<typeof UnsubscribeLandingEnvelopeSchema>,
): UnsubscribeLanding => r.data;
